/**
 * `/o/:org/connect` - client selector, instructions, authorize and test steps,
 * last successful server contact, reconnect and revoke.
 *
 * THE ONE THING THIS SCREEN EXISTS TO GET RIGHT
 *   V1 section 9: "Distinguish configured from verified." They are different
 *   facts and this build can only prove one of them for most people.
 *
 *   CONFIGURED means a client id is registered against this account with a set
 *   of capabilities. That is a row in `app.client_grants`, and every caller can
 *   read their own.
 *
 *   VERIFIED means the server observed an authorised retrieval by that client
 *   against a pinned release: a row in `app.retrieval_events` naming the
 *   client and the release the retrieval was pinned to. It does NOT mean the
 *   bytes arrived. `docs/V1.md:80` — "Retrieval does not establish execution,
 *   success or checkpoint completion" — and `app.retrieval_events` is
 *   append-only with no outcome column, so a failed content read and a
 *   successful one are indistinguishable here. The wording below is bounded by
 *   that, and deliberately says "observed" rather than "served".
 *
 *   The LABEL at the observed branch still reads "Last successful contact"
 *   because `docs/V1.md:361` names that phrase in the wireframe. That tension —
 *   V1:361 against V1:80 — is a spec decision and is NOT resolved here.
 *
 *   `retrieval_events_read` requires an organization admin, so for anybody else
 *   the last contact is genuinely unknowable from here, and the screen says so
 *   rather than reporting the grant date as if it were a test result. Showing a
 *   configured client as working is the specific failure this screen must not
 *   make.
 *
 * WHAT THIS SCREEN NOW DOES ABOUT AUTHORIZING A CLIENT
 *   It links to the consent page, `/o/:org/connect/authorize`, which is where a
 *   person actually grants a client. It used to render Reconnect disabled in
 *   both branches on the stated premise that "this deployment has no OAuth
 *   authorization endpoint in this application" — which was true of this file
 *   and false of the product, and which made a control that could never work
 *   look like one that merely could not be reached today. The page exists now,
 *   so Reconnect is a link to it.
 *
 *   WHAT THE LINK IS AND IS NOT
 *   It carries no PKCE parameters, because PKCE values are generated by the
 *   client, not by this screen: the client builds its own authorization URL,
 *   sends the browser to it, and the consent page renders what the client asked
 *   for. A link from here therefore opens the page in its un-authorized state —
 *   the client id, the organization, and an explicit statement that no PKCE
 *   challenge was presented and so nothing can be granted. That is the
 *   truthful outcome. It is NOT a degraded path standing in for the real one,
 *   and the page says which of the two the visitor is looking at.
 */

import type { AppShell, ScreenState } from '../app.js';
import * as api from '../api/queries.js';
import { type Failure, type Result, ok } from '../api/client.js';
import { type RawHtml, html } from '../lib/html.js';
import { formatDateTime, personLabel } from '../lib/format.js';
import { button, docHead, emptyState, failurePanel, tag } from '../ui/components.js';

export interface ConnectData {
  readonly grants: readonly api.ClientGrantRow[];
  readonly contacts: Readonly<Record<string, ClientContact>>;
  readonly canSeeContacts: boolean;
  readonly endpoint: string;
}

/**
 * What is actually known about one client's last contact.
 *
 * These are THREE facts and they used to share one value. `loadConnect` read
 * `const latest = events.ok ? events.value[0] : undefined`, so a read that FAILED
 * became "no contact recorded" — indistinguishable from a read that succeeded
 * and found nothing. `listClientContacts` went to real trouble to keep those
 * apart (its own docblock: "Separating them is why this is a refusal and not an
 * empty array"), and the caller threw the distinction away.
 *
 * The cost was a sentence that was false rather than merely empty. The server
 * had served that client — there is a row in `app.retrieval_events` for it — and
 * the screen said "Never. No delivery from this client has been observed." That
 * is an execution claim about a delivery the build never looked for, against
 * V1:398 ("Connection success shows the client, tested release and timestamp")
 * and V1:397 ("do not claim they were executed").
 */
export type ClientContact =
  | { readonly kind: 'observed'; readonly at: string; readonly releaseId: string }
  /** The read succeeded and the server has no delivery for this client. */
  | { readonly kind: 'never-observed' }
  /** The read did not succeed. Says nothing about whether a delivery occurred. */
  | { readonly kind: 'unread'; readonly failure: Failure };

/**
 * What to print when the read did not happen.
 *
 * It has to be a sentence that is true in both cases it covers — a deployment
 * with no route, and a deployment whose dependency was unreachable — and in
 * neither case may it contain a word about deliveries. "Unknown" and "no
 * deliveries" are different facts and only the first one is supportable here.
 */
const CONTACT_UNREAD =
  'Unknown. Observed deliveries could not be read, so this screen makes no claim about whether any occurred.';

/**
 * A client is configured the moment a grant row exists; it is VERIFIED when the
 * server has served it. V1 §9:361 requires this screen to distinguish those two,
 * and the distinction is only real if the second one is allowed to be unknown.
 */

export async function loadConnect(app: AppShell, orgId: string): Promise<Result<ConnectData>> {
  const grants = await api.listClientGrants(app.client, orgId, app.state.session?.userId ?? '');
  if (!grants.ok) return grants;

  const isAdmin = app.state.membership?.role === 'admin';
  const contacts: Record<string, ClientContact> = {};
  if (isAdmin) {
    for (const grant of grants.value) {
      if (grant.revoked_at !== null) continue;
      const events = await api.listClientContacts(app.client, orgId, grant.client_id);
      // A failure is carried, not discarded. Collapsing it to `undefined` here
      // is what let `describeContact` print "Never." about a delivery the server
      // had recorded.
      if (!events.ok) {
        contacts[grant.client_id] = { kind: 'unread', failure: events.failure };
        continue;
      }
      const latest = events.value[0];
      contacts[grant.client_id] =
        latest === undefined
          ? { kind: 'never-observed' }
          : { kind: 'observed', at: latest.retrieved_at, releaseId: latest.release_id };
    }
  }

  return ok({
    grants: grants.value,
    contacts,
    canSeeContacts: isAdmin,
    endpoint: `${location.origin}/mcp`,
  });
}

export function renderConnect(app: AppShell, screen: ScreenState): RawHtml {
  if (screen.kind === 'loading' || screen.kind === 'idle') {
    return html`
      ${docHead([{ label: 'Section', value: 'Connect' }], 'Connect')}
      <div class="panel"><p class="cell-note">Loading client grants.</p></div>
    `;
  }
  if (screen.kind === 'failed') {
    return html`
      ${docHead([{ label: 'Section', value: 'Connect' }], 'Connect')}
      ${failurePanel(screen.failure, { retry: 'connect:reload' })}
    `;
  }

  const data = screen.data as ConnectData;
  const active = data.grants.filter((grant) => grant.revoked_at === null);

  return html`
    ${docHead(
      [
        { label: 'Section', value: 'Connect' },
        { label: 'Organization', value: app.orgSlug },
        { label: 'Clients', value: String(active.length) },
      ],
      'Connect a client',
      'An agent client reads skills over the organization MCP endpoint using your own permissions. It never receives more than you can read, and it cannot write.',
    )}

    <div class="split">
      <div>
        ${data.grants.length === 0
          ? html`<div class="panel">
              ${emptyState({
                title: 'No client is connected',
                body: 'Nothing has been authorized for your account in this organization. A client asks to connect by sending your browser to the authorization page, where it names the actions it wants and you decide which to grant.',
                primary: null,
                secondary: [{ label: 'Ask an administrator', action: 'connect:goto-admin' }],
              })}
            </div>`
          : data.grants.map((grant) => grantCard(app, data, grant))}
        ${authorizeAction(app)}
      </div>
      <div>
        ${instructionsPanel(data)}
        ${data.canSeeContacts ? '' : contactsHiddenPanel()}
      </div>
    </div>
  `;
}

function grantCard(app: AppShell, data: ConnectData, grant: api.ClientGrantRow): RawHtml {
  const revoked = grant.revoked_at !== null;
  const contact = data.contacts[grant.client_id];
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2><code>${grant.client_id}</code></h2>
        ${revoked ? tag('Revoked', 'X', 'danger') : tag('Configured', 'C', 'accent')}
      </div>
      <div class="panel-body stack-sm">
        <dl class="dl">
          <dt>Capabilities</dt>
          <dd>${grant.capabilities.join(', ')}</dd>
          <dt>Granted to</dt>
          <dd>${personLabel(grant.actor_id, app.state.directory)}</dd>
          <dt>Registered</dt>
          <dd>${formatDateTime(grant.created_at)}</dd>
          <dt>Revoked</dt>
          <dd>${revoked ? formatDateTime(grant.revoked_at ?? '') : 'no'}</dd>
          <dt>Last successful contact</dt>
          <dd>${describeContact(contact, data.canSeeContacts, revoked)}</dd>
        </dl>
        <div class="btn-row">
          ${reconnectLink(app, grant)}
          ${revoked
            ? ''
            : button('Revoke', `connect:revoke:${grant.client_id}`, 'danger')}
        </div>
        <p class="cell-note">
          Revoking takes effect on that client's next request. It stops reads as well as writes: a
          revoked grant fails the live-grant gate on every read path.
        </p>
      </div>
    </div>
  `;
}

/**
 * Reconnect, as a link to the consent page rather than a permanently disabled
 * button.
 *
 * It carried `client_id` and nothing else, because PKCE values belong to the
 * client: the page it opens therefore names the client and the organization
 * and states that no challenge was presented. Both are true, and saying so is
 * better than a control that looks live and cannot do anything. A client that
 * builds its own authorization URL arrives at the same page carrying a
 * challenge, and that is the path that grants.
 */
function reconnectLink(app: AppShell, grant: api.ClientGrantRow): RawHtml {
  const href = `/o/${app.orgSlug}/connect/authorize?client_id=${encodeURIComponent(grant.client_id)}`;
  return html`
    <a class="btn btn-secondary" href="${href}">
      ${grant.revoked_at === null ? 'Reconnect' : 'Authorize again'}
      <span class="visually-hidden">${grant.client_id}</span>
    </a>
  `;
}

/**
 * The one primary action on this screen: authorize a client.
 *
 * `emptyState` takes actions, not links, and its `primary` is the screen's
 * single filled button by DESIGN.md:183. The client id is read from the page's
 * own field rather than from the URL, so nothing about which organization the
 * grant lands in is asserted here — that is `identity.orgId`, server-side.
 */
function authorizeAction(app: AppShell): RawHtml {
  const href = `/o/${app.orgSlug}/connect/authorize`;
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Authorize a client</h2></div>
      <div class="panel-body stack-sm">
        <p class="cell-note">
          Consent is granted by a person, in a browser, for one organization at a time. The client
          sends the browser to the authorization page carrying the actions it wants; you read them
          here and decide. A client starts read-only and is never granted proposal or draft
          capability unless it asked for it and you agreed.
        </p>
        <div class="btn-row">
          <a class="btn btn-primary" href="${href}">
            Open the authorization page
            <span class="visually-hidden">for ${app.orgSlug}</span>
          </a>
        </div>
      </div>
    </div>
  `;
}

/**
 * The last-contact line.
 *
 * Four sentences, and each is true of exactly one situation. The order matters:
 * the caller is told they may not look before they are told what looking found,
 * and a revoked grant is described from its own row rather than from a
 * delivery history nobody is going to read.
 */
function describeContact(
  contact: ClientContact | undefined,
  canSeeContacts: boolean,
  revoked: boolean,
): string {
  if (!canSeeContacts) {
    return 'Not visible to you. Reading observed deliveries requires an organization administrator.';
  }
  if (revoked) {
    return 'Not checked. This grant is revoked, and a revoked grant records no further deliveries.';
  }
  if (contact === undefined) return CONTACT_UNREAD;
  switch (contact.kind) {
    case 'observed':
      // V1:398 wants the client, the tested release AND the timestamp.
      //
      // WHAT THIS DOES NOT SAY, because the data cannot support it: that the
      // release was served. `app.retrieval_events` records an authorised
      // retrieval ATTEMPT against a pinned release — V1:80 is explicit that
      // "retrieval does not establish execution, success or checkpoint
      // completion" — and `tools.ts:459` writes the row before the bytes are
      // read. A failed content read still produces a row, so "served" and
      // "attempted" are indistinguishable here and this line must not imply the
      // first one. Naming the pinned release is still what distinguishes a
      // verification from a liveness ping; it just does not verify anything.
      //
      // The label above this field is V1:361's own phrase ("last successful
      // server contact") and collides with V1:80. That tension is unresolved
      // and belongs to the spec owner, not to a wording change in this file.
      // The label above keeps V1:361's phrase, so the limit is stated where a
      // person reads it and not only in this comment.
      return (
        `${formatDateTime(contact.at)} (release ${contact.releaseId})` +
        ' — the server recorded this retrieval. It does not record whether ' +
        'the content was served.'
      );
    case 'never-observed':
      return 'Never. No delivery from this client has been observed.';
    case 'unread':
      return CONTACT_UNREAD;
  }
}

function contactsHiddenPanel(): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head"><h2>Observed deliveries</h2></div>
      <div class="panel-body">
        <p class="cell-note">
          Delivery observations name the client and the immutable release it was served. Reading them
          is an administrator action, so this screen can report that a client is configured but cannot
          confirm that it has ever reached the server.
        </p>
      </div>
    </div>
  `;
}

function instructionsPanel(data: ConnectData): RawHtml {
  return html`
    <div class="panel">
      <div class="panel-head"><h2>How a client connects</h2></div>
      <div class="panel-body stack-sm">
        <ol class="steps" style="list-style: decimal; padding-left: 18px;">
          <li>Install the connector in the client you want to use.</li>
          <li>
            Point it at <code>${data.endpoint}</code> over HTTPS. The endpoint authenticates every
            request; there is no shared secret in configuration.
          </li>
          <li>
            The client sends your browser to this organization's authorization page carrying the
            actions it wants. You read them and grant or refuse; the grant is recorded against this
            organization and your account, and a client that asks for nothing gets read-only access.
          </li>
          <li>
            Ask the client to list skills. A client that cannot list anything is configured and not
            verified, and the difference is the line above.
          </li>
        </ol>
        <div class="callout">
          <span class="callout-tag">Endpoint</span>
          <p>
            Streamable HTTP at <code>/mcp</code>. Issuer, audience and expiry are checked on every
            request. An unrelated sign-in token is not accepted as a client credential.
          </p>
        </div>
      </div>
    </div>
  `;
}